// caiet UC50-14 — "Operatorul poate căuta în rândurile raportului draft cel puțin
// după medicament, lot și motiv. Filtrul va include și opțiunea de afișare doar a
// rândurilor cu eroare." ("The Operator can search the draft report's rows by at
// least medicine, lot and reason. The filter also includes an option to show only
// rows with an error.")
//
// A pure function, so the caiet's three search fields are provable without a DOM
// and the screen keeps one `useMemo` instead of a filtering pass per facet.
import { stockMovementReasonSchema, type StockMovementReason } from '@simsm/shared-types';
import type { DraftRow } from './api';

export interface ReportRowCriteria {
  /** Free text, matched against the row's medicine, lot and reason. */
  query: string;
  errorsOnly: boolean;
  newLotsOnly: boolean;
  correctionsOnly: boolean;
}

export const NO_ROW_CRITERIA: ReportRowCriteria = {
  query: '',
  errorsOnly: false,
  newLotsOnly: false,
  correctionsOnly: false,
};

// Romanian diacritics are the norm in the nomenclator and the exception on a
// keyboard, so "sirop" has to find "Sirop" and "Comercializare" has to find
// "comercializare". The wireframe's own `norm` (lib/cautare.ts) is this.
function fold(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** The reason as the screen shows it; a code the schema does not know stays as written. */
function shownReason(row: DraftRow, reasonLabel: (reason: StockMovementReason) => string): string {
  const parsed = stockMovementReasonSchema.safeParse(row.reason);
  return parsed.success ? reasonLabel(parsed.data) : (row.reason ?? '');
}

/**
 * "corectare" (UC50-05): a row that corrects a reported movement. A row filed as
 * Corectare with no movement named is an inventory adjustment (CF-59, sense 1),
 * and is not marked (TC-UC50-05-01).
 */
export function isCorrectionRow(row: DraftRow): boolean {
  return Boolean(row.correctionOf);
}

function matchesQuery(
  row: DraftRow,
  folded: string,
  reasonLabel: (reason: StockMovementReason) => string,
): boolean {
  const haystack = [
    row.medicineLabel ?? '',
    row.medicineCode ?? '',
    row.lot ?? '',
    shownReason(row, reasonLabel),
  ];
  return haystack.some((field) => fold(field).includes(folded));
}

/**
 * The rows a report shows under the toolbar's search and facets.
 *
 * `reasonLabel` translates the shown reason, because the operator searches for
 * "distrugere", not for `DESTRUCTION`: the screen holds the translations, this
 * module holds the rule.
 */
export function filterReportRows(
  rows: DraftRow[],
  criteria: ReportRowCriteria,
  reasonLabel: (reason: StockMovementReason) => string = (reason) => reason,
): DraftRow[] {
  const folded = fold(criteria.query.trim());
  return rows.filter((row) => {
    if (criteria.errorsOnly && row.validationErrors.length === 0) return false;
    if (criteria.newLotsOnly && row.lotMatch !== 'NEW') return false;
    if (criteria.correctionsOnly && !isCorrectionRow(row)) return false;
    if (folded.length > 0 && !matchesQuery(row, folded, reasonLabel)) return false;
    return true;
  });
}

/** A row that names its medicine by code alone, as an imported row does (spec 054 T099). */
export function lacksMedicineName(row: DraftRow): boolean {
  return (
    Boolean(row.medicineCode) && (!row.medicineLabel || row.medicineLabel === row.medicineCode)
  );
}

/**
 * The rows as the report shows them (spec 054 T097, T099).
 *
 * The rows on screen stay as the operator left them, a change not saved yet
 * included (constitution art. III). But a row's errors are the server's: after
 * a save that failed, the server still holds the error the operator fixed, and
 * the report says so until a save succeeds (art. IV). A row the server has not
 * seen yet keeps its own. A row named by its code alone takes the catalogue's
 * name, for display.
 */
export function shownReportRows(
  rows: readonly DraftRow[],
  serverRows: readonly DraftRow[],
  names: ReadonlyMap<string, string>,
): DraftRow[] {
  const held = new Map(serverRows.map((row) => [row.clientRowId, row]));
  return rows.map((row) => {
    const server = held.get(row.clientRowId);
    const name = lacksMedicineName(row) ? names.get(row.medicineCode ?? '') : undefined;
    if (!server && name === undefined) return row;
    const shown: DraftRow = { ...row };
    if (server) {
      shown.validationErrors = server.validationErrors;
      if (server.sourceIssues) shown.sourceIssues = server.sourceIssues;
      else delete shown.sourceIssues;
    }
    if (name !== undefined) shown.medicineLabel = name;
    return shown;
  });
}

export interface ReportRowCounts {
  total: number;
  withErrors: number;
  newLots: number;
  corrections: number;
}

export function rowCounts(rows: DraftRow[]): ReportRowCounts {
  return {
    total: rows.length,
    withErrors: rows.filter((row) => row.validationErrors.length > 0).length,
    newLots: rows.filter((row) => row.lotMatch === 'NEW').length,
    corrections: rows.filter(isCorrectionRow).length,
  };
}
